import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useCMS } from '../../context/CMSContext';
import { toast, Toaster } from 'sonner';
import { Save, RefreshCw, Plus, X, Image as ImageIcon, Eye } from 'lucide-react';

const AdminHeroPage = () => {
  const { getHeroContent, updateHeroContent } = useCMS();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState({
    badge_text: '',
    title_line1: '',
    title_line2: '',
    title_line3: '',
    subtitle: '',
    hero_image: '',
    cta_primary_text: '',
    cta_secondary_text: '',
    value_props: []
  });
  const [newValueProp, setNewValueProp] = useState('');

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const data = await getHeroContent();
      setContent(data);
    } catch (e) {
      toast.error('Failed to load hero content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateHeroContent(content);
      toast.success('Hero content saved successfully!');
    } catch (e) {
      toast.error('Failed to save hero content');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  const addValueProp = () => {
    if (newValueProp.trim()) {
      setContent(prev => ({
        ...prev,
        value_props: [...(prev.value_props || []), newValueProp.trim()]
      }));
      setNewValueProp('');
    }
  };

  const removeValueProp = (index) => {
    setContent(prev => ({
      ...prev,
      value_props: prev.value_props.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return <div className="text-text-secondary">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <Toaster position="top-right" richColors />
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Hero Section</h1>
          <p className="text-text-secondary mt-1">Edit your homepage hero content</p>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="outline"
            onClick={loadContent}
            className="border-border-line"
          >
            <RefreshCw size={18} className="mr-2" />
            Reset
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-tech-blue hover:bg-tech-blue/90"
          >
            <Save size={18} className="mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Editor */}
        <div className="space-y-6">
          {/* Badge */}
          <Card className="border-border-line">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Badge Text</CardTitle>
            </CardHeader>
            <CardContent>
              <input
                type="text"
                value={content.badge_text || ''}
                onChange={(e) => handleChange('badge_text', e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-border-line bg-surface text-text-primary focus:ring-2 focus:ring-tech-blue focus:border-transparent"
                placeholder="e.g., Built for Australian NDIS Providers"
              />
            </CardContent>
          </Card>

          {/* Title Lines */}
          <Card className="border-border-line">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Main Title</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm text-text-secondary mb-2">Line 1</label>
                <input
                  type="text"
                  value={content.title_line1 || ''}
                  onChange={(e) => handleChange('title_line1', e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-border-line bg-surface text-text-primary focus:ring-2 focus:ring-tech-blue focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-2">Line 2 (Highlighted)</label>
                <input
                  type="text"
                  value={content.title_line2 || ''}
                  onChange={(e) => handleChange('title_line2', e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-border-line bg-surface text-text-primary focus:ring-2 focus:ring-tech-blue focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-2">Line 3</label>
                <input
                  type="text"
                  value={content.title_line3 || ''}
                  onChange={(e) => handleChange('title_line3', e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-border-line bg-surface text-text-primary focus:ring-2 focus:ring-tech-blue focus:border-transparent"
                />
              </div>
            </CardContent>
          </Card>

          {/* Subtitle */}
          <Card className="border-border-line">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Subtitle</CardTitle>
            </CardHeader>
            <CardContent>
              <textarea
                value={content.subtitle || ''}
                onChange={(e) => handleChange('subtitle', e.target.value)}
                rows={3}
                className="w-full px-4 py-3 rounded-lg border border-border-line bg-surface text-text-primary focus:ring-2 focus:ring-tech-blue focus:border-transparent resize-none"
              />
            </CardContent>
          </Card>

          {/* Hero Image */}
          <Card className="border-border-line">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center">
                <ImageIcon size={20} className="mr-2" />
                Hero Image
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <input
                type="text"
                value={content.hero_image || ''}
                onChange={(e) => handleChange('hero_image', e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-border-line bg-surface text-text-primary focus:ring-2 focus:ring-tech-blue focus:border-transparent"
                placeholder="https://example.com/image.jpg"
              />
              {content.hero_image && (
                <img 
                  src={content.hero_image} 
                  alt="Hero preview" 
                  className="w-full h-40 object-cover rounded-lg"
                />
              )}
            </CardContent>
          </Card>

          {/* CTA Buttons */}
          <Card className="border-border-line">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Call-to-Action Buttons</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm text-text-secondary mb-2">Primary Button</label>
                <input
                  type="text"
                  value={content.cta_primary_text || ''}
                  onChange={(e) => handleChange('cta_primary_text', e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-border-line bg-surface text-text-primary focus:ring-2 focus:ring-tech-blue focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-2">Secondary Button</label>
                <input
                  type="text"
                  value={content.cta_secondary_text || ''}
                  onChange={(e) => handleChange('cta_secondary_text', e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-border-line bg-surface text-text-primary focus:ring-2 focus:ring-tech-blue focus:border-transparent"
                />
              </div>
            </CardContent>
          </Card>

          {/* Value Props */}
          <Card className="border-border-line">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Value Propositions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newValueProp}
                  onChange={(e) => setNewValueProp(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addValueProp()}
                  className="flex-1 px-4 py-3 rounded-lg border border-border-line bg-surface text-text-primary focus:ring-2 focus:ring-tech-blue focus:border-transparent"
                  placeholder="Add new value prop..."
                />
                <Button onClick={addValueProp} className="bg-tech-blue hover:bg-tech-blue/90">
                  <Plus size={18} />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {(content.value_props || []).map((prop, index) => (
                  <span 
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full bg-care-teal/10 text-care-teal border border-care-teal/20"
                  >
                    {prop}
                    <button 
                      onClick={() => removeValueProp(index)}
                      className="ml-2 hover:text-red-500"
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Preview */}
        <div className="lg:sticky lg:top-8">
          <Card className="border-border-line overflow-hidden">
            <CardHeader className="pb-3 bg-page-bg">
              <CardTitle className="text-lg flex items-center">
                <Eye size={20} className="mr-2" />
                Live Preview
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="bg-page-bg p-6 min-h-[500px]">
                {/* Badge */}
                {content.badge_text && (
                  <div className="inline-flex items-center px-3 py-1 bg-soft-sky rounded-full border border-tech-blue/20 mb-4">
                    <span className="text-xs font-medium text-text-primary">{content.badge_text}</span>
                  </div>
                )}
                
                {/* Title */}
                <h1 className="text-2xl md:text-3xl font-bold text-primary-navy leading-tight mb-4">
                  {content.title_line1}<br/>
                  <span className="text-tech-blue">{content.title_line2}</span><br/>
                  {content.title_line3}
                </h1>

                {/* Subtitle */}
                <p className="text-sm text-text-secondary mb-4 leading-relaxed">
                  {content.subtitle}
                </p>

                {/* Value Props */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {(content.value_props || []).map((prop, index) => (
                    <span key={index} className="inline-flex items-center px-2 py-1 bg-care-teal/10 rounded-full text-xs text-text-primary">
                      ✓ {prop}
                    </span>
                  ))}
                </div>

                {/* CTAs */}
                <div className="flex gap-2 mb-4">
                  <button className="px-4 py-2 bg-tech-blue text-white text-sm rounded-lg">
                    {content.cta_primary_text || 'Primary CTA'}
                  </button>
                  <button className="px-4 py-2 border border-tech-blue text-tech-blue text-sm rounded-lg">
                    {content.cta_secondary_text || 'Secondary CTA'}
                  </button>
                </div>

                {/* Hero Image */}
                {content.hero_image && (
                  <img 
                    src={content.hero_image} 
                    alt="Hero" 
                    className="w-full h-48 object-cover rounded-xl"
                  />
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminHeroPage;
